import { useQuery } from '@tanstack/react-query';
import { useLatestWearableMetrics } from './use-wearable-metrics';

export interface WearableAverages {
  avgSleepDuration: number;
  avgSleepEfficiency: number;
  avgHRV: number;
  avgRHR: number;
  avgSleepOnset: number | null;
  avgWakeupTime: number | null;
  dataPoints: number;
  hasEnoughData: boolean;
}

export const useWearableAverages = () => {
  const { data: metrics = [] } = useLatestWearableMetrics();

  return useQuery({
    queryKey: ['wearable-averages', metrics],
    queryFn: (): WearableAverages => {
      if (!metrics || metrics.length === 0) {
        return {
          avgSleepDuration: 0,
          avgSleepEfficiency: 0,
          avgHRV: 0,
          avgRHR: 0,
          avgSleepOnset: null,
          avgWakeupTime: null,
          dataPoints: 0,
          hasEnoughData: false
        };
      }

      // Get last 14 days of data
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

      const recent14DaysMetrics = metrics.filter(metric => {
        const recordDate = new Date(metric.record_date);
        return recordDate >= fourteenDaysAgo;
      });

      if (recent14DaysMetrics.length < 10) { // Need at least 10 days of data
        return {
          avgSleepDuration: 0,
          avgSleepEfficiency: 0,
          avgHRV: 0,
          avgRHR: 0,
          avgSleepOnset: null,
          avgWakeupTime: null,
          dataPoints: recent14DaysMetrics.length,
          hasEnoughData: false
        };
      }

      // Calculate averages
      const sleepDurations = recent14DaysMetrics
        .filter(m => m.sleep_duration != null)
        .map(m => m.sleep_duration!);
      
      const sleepEfficiencies = recent14DaysMetrics
        .filter(m => m.sleep_efficiency != null)
        .map(m => m.sleep_efficiency!);
      
      const hrvValues = recent14DaysMetrics
        .filter(m => m.hrv != null)
        .map(m => m.hrv!);
      
      const rhrValues = recent14DaysMetrics
        .filter(m => m.rhr != null)
        .map(m => m.rhr!);

      // Helper: circular mean for time-of-day in seconds
      function circularMean(times: number[]): number {
        const angles = times.map(sec => (sec / 86400) * 2 * Math.PI);
        const sumSin = angles.reduce((sum, a) => sum + Math.sin(a), 0);
        const sumCos = angles.reduce((sum, a) => sum + Math.cos(a), 0);
        const meanAngle = Math.atan2(sumSin / times.length, sumCos / times.length);
        let meanSec = (meanAngle / (2 * Math.PI)) * 86400;
        if (meanSec < 0) meanSec += 86400;
        return meanSec;
      }

      const sleepOnsetSeconds = recent14DaysMetrics
        .filter(m => m.sleep_onset != null)
        .map(m => {
          const date = new Date(m.sleep_onset!);
          console.log('[WearableAverages] sleep_onset raw:', m.sleep_onset, 'Date:', date, 'Local:', date.getHours() + ':' + date.getMinutes() + ':' + date.getSeconds());
          return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
        });

      const avgSleepOnset = sleepOnsetSeconds.length > 0
        ? circularMean(sleepOnsetSeconds)
        : null;

      const wakeupTimeSeconds = recent14DaysMetrics
        .filter(m => m.wakeup_time != null)
        .map(m => {
          const date = new Date(m.wakeup_time!);
          console.log('[WearableAverages] wakeup_time raw:', m.wakeup_time, 'Date:', date, 'Local:', date.getHours() + ':' + date.getMinutes() + ':' + date.getSeconds());
          return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
        });

      const avgWakeupTime = wakeupTimeSeconds.length > 0
        ? circularMean(wakeupTimeSeconds)
        : null;

      const avgSleepDuration = sleepDurations.length > 0 
        ? sleepDurations.reduce((sum, val) => sum + val, 0) / sleepDurations.length 
        : 0;

      const avgSleepEfficiency = sleepEfficiencies.length > 0 
        ? sleepEfficiencies.reduce((sum, val) => sum + val, 0) / sleepEfficiencies.length 
        : 0;

      const avgHRV = hrvValues.length > 0 
        ? hrvValues.reduce((sum, val) => sum + val, 0) / hrvValues.length 
        : 0;

      const avgRHR = rhrValues.length > 0 
        ? rhrValues.reduce((sum, val) => sum + val, 0) / rhrValues.length 
        : 0;

      return {
        avgSleepDuration,
        avgSleepEfficiency,
        avgHRV,
        avgRHR,
        avgSleepOnset,
        avgWakeupTime,
        dataPoints: recent14DaysMetrics.length,
        hasEnoughData: true
      };
    },
    enabled: metrics.length > 0,
  });
};
